import { useAuth as useSDKAuth } from '@actschurch/ccms-sdk';
import { useMemo, useCallback } from 'react';
import {
  enrichUser,
  type ExtendedUser,
  hasDomainAccess as checkDomainAccess,
  getUserDisplayName,
  getUserInitials,
} from '../types/auth';

/**
 * Enriched Auth Hook Return Type
 *
 * Provides backward-compatible interface with legacy useAuthStore
 * while using SDK's authentication under the hood.
 */
export interface UseEnrichedAuthReturn {
  // State
  user: ExtendedUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  isInitializing: boolean;
  error: string | null;

  // Actions
  login: (email: string, password: string, rememberMe?: boolean) => Promise<void>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;

  // Permission checks
  hasRole: (role: string) => boolean;
  hasAnyRole: (roles: string[]) => boolean;
  hasAllRoles: (roles: string[]) => boolean;
  hasPermission: (permission: string) => boolean;
  hasAnyPermission: (permissions: string[]) => boolean;
  hasAllPermissions: (permissions: string[]) => boolean;
  hasDomainAccess: (domain: string) => boolean;

  // Helpers
  getUserDisplayName: () => string;
  getUserInitials: () => string;
}

/**
 * Enriched Auth Hook
 *
 * Wraps SDK's useAuth() with application-specific user enrichment
 * and backward-compatible API for easy migration from legacy authStore.
 *
 * Key features:
 * - Maps SDK's LoggedOnUser to ExtendedUser
 * - Renames 'rights' to 'permissions' for consistency
 * - Adds 'demo' domain automatically
 * - Provides permission/role check helpers
 * - Combines isLoading and isInitializing flags
 *
 * @returns UseEnrichedAuthReturn with user state and auth actions
 */
export function useEnrichedAuth(): UseEnrichedAuthReturn {
  // Get SDK auth state
  const sdkAuth = useSDKAuth();
  const { state, login: sdkLogin, logout: sdkLogout, hasRole, hasRight } = sdkAuth;
  // refresh might not exist in older SDK versions
  const sdkRefresh = 'refresh' in sdkAuth ? sdkAuth.refresh : undefined;

  // Enrich SDK user with application-specific fields
  const user = useMemo(() => {
    if (!state.user) return null;
    return enrichUser(state.user);
  }, [state.user]);

  // Login action (wraps SDK login)
  const login = useCallback(
    async (email: string, password: string, rememberMe = false) => {
      try {
        await sdkLogin(email, password, rememberMe);
      } catch (error) {
        // SDK will set error in state, we just re-throw for caller
        throw error;
      }
    },
    [sdkLogin]
  );

  // Logout action (wraps SDK logout)
  const logout = useCallback(async () => {
    try {
      await sdkLogout();
    } catch (error) {
      console.error('[useEnrichedAuth] Logout error:', error);
      throw error;
    }
  }, [sdkLogout]);

  // Refresh token action (wraps SDK refresh)
  const refresh = useCallback(async () => {
    if (!sdkRefresh) {
      console.warn('[useEnrichedAuth] Refresh not available in SDK');
      return;
    }
    try {
      await sdkRefresh();
    } catch (error) {
      console.error('[useEnrichedAuth] Refresh error:', error);
      throw error;
    }
  }, [sdkRefresh]);

  // Permission check: single permission
  const hasPermission = useCallback(
    (permission: string) => {
      // Use SDK's hasRight (SDK uses 'rights' terminology)
      return hasRight(permission);
    },
    [hasRight]
  );

  // Permission check: any permission
  const hasAnyPermission = useCallback(
    (permissions: string[]) => {
      return permissions.some((p) => hasRight(p));
    },
    [hasRight]
  );

  // Permission check: all permissions
  const hasAllPermissions = useCallback(
    (permissions: string[]) => {
      return permissions.every((p) => hasRight(p));
    },
    [hasRight]
  );

  // Role check: any role
  const hasAnyRoleCheck = useCallback(
    (roles: string[]) => {
      return roles.some((r) => hasRole(r));
    },
    [hasRole]
  );

  // Role check: all roles
  const hasAllRolesCheck = useCallback(
    (roles: string[]) => {
      return roles.every((r) => hasRole(r));
    },
    [hasRole]
  );

  // Domain access check
  const hasDomainAccessCheck = useCallback(
    (domain: string) => {
      return checkDomainAccess(user, domain);
    },
    [user]
  );

  // Get user display name
  const getDisplayName = useCallback(() => {
    return getUserDisplayName(user);
  }, [user]);

  // Get user initials
  const getInitials = useCallback(() => {
    return getUserInitials(user);
  }, [user]);

  return {
    // State
    user,
    isAuthenticated: state.isAuthenticated,
    isLoading: state.isLoading,
    isInitializing: state.isInitializing,
    error: state.error,

    // Actions
    login,
    logout,
    refresh,

    // Permission checks
    hasRole,
    hasAnyRole: hasAnyRoleCheck,
    hasAllRoles: hasAllRolesCheck,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    hasDomainAccess: hasDomainAccessCheck,

    // Helpers
    getUserDisplayName: getDisplayName,
    getUserInitials: getInitials,
  };
}

/**
 * Alias for backward compatibility
 *
 * Components can import as:
 * - import { useEnrichedAuth } from '@/hooks/useEnrichedAuth'
 * - import { useAuth } from '@/hooks/useEnrichedAuth'
 */
export const useAuth = useEnrichedAuth;
